// src/components/ui/PageBand.jsx
//
// The page header of the admin pages — Gestione Workspace, Knowledge Base (tenant-admin and member),
// Brand Voice, Buyer Personas. ⭐ 2026-10-01 (v193): it is PORTALLED into the shell Header's slot
// (`PAGE_HEADER_SLOT_ID`, in Header.jsx), exactly as the Content Generator portals its title there — so
// the title sits on the avatar's row, on the header's own grey background (no white band), and stays in
// place while `main` scrolls. It holds the page's ONE <h1> (`PAGE_TITLE`, the Content Generator's title
// style, shared), an optional element at the far right of the slot (`aside` — the personalization badge; v201:
// moved there from beside the title), an optional control after it (`action` — the console's company selector), an
// optional line of text underneath (`subtitle`) and an optional row below (`children` — the tabs, on a thin grey
// line).
// ⚠️ With no slot in the document (a page rendered without the shell, i.e. the test suites) it renders IN
// PLACE instead — same markup, so the page still has its <h1>.
// Also used by Report Attività (/dashboard), Storico Generazioni (/runs, /tenant-admin/runs) and the
// Guida. The superadmin KB / Personas pages keep their AdminLayout chrome and do not use it.
//
// ⭐ The TABS share ONE look through the class `PAGE_BAND_TAB` (a CSS class, because inline styles cannot
// express hover or focus): the console's NavLinks (active = `aria-current="page"`) and Brand Voice's
// section BUTTONS (active = `aria-selected="true"`, SegmentedTabs `variant="band"` — ⛔ buttons there,
// never links: that page's leave guard intercepts in-app link clicks). 15px, weight 500, a hover state, a
// 3px accent underline sitting on the tab row's line, a focus ring.
import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';

/** The shell Header's slot a page title is portalled into (Header.jsx renders it; the Content
 *  Generator and PageBand both use it). */
export const PAGE_HEADER_SLOT_ID = 'page-header-slot';

/** v201: the height of the header's FIRST row — the user avatar's size (Header.jsx) and the title's minimum height.
 *  The header aligns its two halves to the top, so the title (and the badge beside it) and the avatar share one
 *  line on every page, however tall the rest of the page header is (a subtitle, a tab row, a breadcrumb). */
export const HEADER_ROW_HEIGHT = 38;

/** The page-title typography — the Content Generator's, shared so the two can never differ. v201: at least one
 *  header row tall, its text centred in it (see HEADER_ROW_HEIGHT). */
export const PAGE_TITLE = {
  margin: 0, fontSize: '26px', fontWeight: 800, color: COLORS.ink,
  fontFamily: FONT_SANS, letterSpacing: '-0.5px', lineHeight: 1.1,
  minHeight: HEADER_ROW_HEIGHT, display: 'flex', alignItems: 'center',
};

export const PAGE_BAND_TAB = 'page-band-tab';

const TAB_CSS = `
  .${PAGE_BAND_TAB} {
    display: inline-flex; align-items: center; padding: 10px 18px; margin-bottom: -1px;
    border: none; border-bottom: 3px solid transparent; background: transparent;
    font-family: ${FONT_SANS}; font-size: 15px; font-weight: 500; color: ${COLORS.textSecondary};
    text-decoration: none; white-space: nowrap; cursor: pointer;
    transition: color 0.15s, border-color 0.15s;
  }
  .${PAGE_BAND_TAB}:hover { color: ${COLORS.ink}; border-bottom-color: ${COLORS.borderStrong}; }
  .${PAGE_BAND_TAB}[aria-current="page"], .${PAGE_BAND_TAB}[aria-selected="true"] {
    color: ${COLORS.accent}; font-weight: 600; border-bottom-color: ${COLORS.accent};
  }
  .${PAGE_BAND_TAB}:focus-visible {
    outline: 2px solid ${COLORS.accent}; outline-offset: -2px; border-radius: ${RADII.controlInner};
  }
`;

function BandContent({ title, aside, action, subtitle, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, width: '100%' }}>
      <style>{TAB_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        {/* v201: the aside (the personalization badge) and `action` sit at the RIGHT END of the row, left of the
            avatar — a fixed spot on every page, whatever the title's length (beside the title it moved from page
            to page). OUTSIDE the <h1>, so the heading keeps its own accessible name. The row wraps on a narrow
            screen, so they drop below the title rather than overlap it. */}
        <h1 style={PAGE_TITLE}>{title}</h1>
        {(aside || action) && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            {aside}
            {action}
          </div>
        )}
      </div>
      {subtitle && (
        <p style={{ margin: 0, fontFamily: FONT_SANS, fontSize: '13px', color: COLORS.textSecondary }}>
          {subtitle}
        </p>
      )}
      {children && (
        // The tab row keeps a thin line under it, so the active 3px underline has something to sit on.
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px',
          borderBottom: `1px solid ${COLORS.borderStrong}`,
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function PageBand(props) {
  const [slot, setSlot] = useState(null);
  // Layout effect: the slot is found before the browser paints, so the header never flashes in place.
  useLayoutEffect(() => { setSlot(document.getElementById(PAGE_HEADER_SLOT_ID)); }, []);
  if (slot) return createPortal(<BandContent {...props} />, slot);
  return (
    <div style={{ padding: '28px 40px 20px' }}>
      <BandContent {...props} />
    </div>
  );
}
